import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from '@core/api/api.service';

/**
 * Result interface for marketplace language change operations
 */
export interface MarketplaceLanguageChangeResult {
  data: any[];
  success: boolean;
  errors?: string[];
  context: 'emittent' | 'investor' | 'client' | 'page-reload';
}

/**
 * Service responsible for reloading page data when language changes.
 *
 * This service handles the actual API calls to reload data endpoints
 * with the new language. The language header is automatically added
 * by the ApiConfigInterceptor.
 */
@Injectable({
  providedIn: 'root',
})
export class MarketplaceLanguageChangeService {
  constructor(private apiService: ApiService) {}

  /**
   * Reloads the current page's data endpoint when language changes
   * Language will be automatically added as a header by the existing system
   * @param endpoint - The endpoint to reload (e.g., /api/investors/.../products)
   * @returns Observable with the reloaded data
   */
  public reloadPageData(endpoint: string): Observable<MarketplaceLanguageChangeResult> {
    // The ApiConfigInterceptor will automatically prepend the apiUrl
    // So we just need to pass the endpoint path directly
    return this.apiService.get<any[]>(endpoint).pipe(
      map((data) => ({
        data,
        success: true,
        context: 'page-reload' as const, // Specific context for page data reload
      })),
      catchError((error) => {
        console.error('Error reloading page data:', error);
        return of({
          data: [],
          success: false,
          errors: [error.message || 'Unknown error'],
          context: 'page-reload' as const,
        });
      }),
    );
  }
}
